import PropTypes from 'prop-types'
import { forwardRef, useCallback, useRef } from 'react'
import { SnackbarProvider as NotistackProvider, SnackbarContent, closeSnackbar } from 'notistack'
// @mui
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'
//
import { Card, CircularProgress, Typography } from '@mui/material'
import { Stack } from '@mui/system'
import { useTheme } from '@emotion/react'
import Iconify from '../iconify'
import { useSettingsContext } from '../settings'
//
import { StyledIcon, StyledNotistack } from './styles'

// ----------------------------------------------------------------------

const LoadingNotistack = forwardRef((props, ref) => {
  const handleDismiss = useCallback(() => {
    closeSnackbar(props.id)
  }, [props.id])

  const theme = useTheme()

  const style = {
    padding: '8px 4px 8px 8px',
    color: theme.palette.text.primary,
    boxShadow: '0 8px 16px 0 rgba(0, 0, 0, 0.16)',
    borderRadius: '8px',
    backgroundColor: `${theme.palette.background.paper} !important`,
    width: '100%',
  }

  return (
    <SnackbarContent ref={ref}>
      <Card sx={style} style={{ backgroundColor: '#fddc6c' }}>
        <Stack
          direction='row'
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Stack direction='row' alignItems='center' gap={1.5}>
            <CircularProgress color='primary' />
            <Typography variant='subtitle2'>{props.message}</Typography>
          </Stack>
          <IconButton id='loadingMsgClose' size='small' sx={{ ml: 1 }} onClick={handleDismiss}>
            <Iconify icon='mdi:close' />
          </IconButton>
        </Stack>
      </Card>
    </SnackbarContent>
  )
})

LoadingNotistack.propTypes = {
  id: PropTypes.any,
  message: PropTypes.string,
}

export default function SnackbarProvider({ children }) {
  const settings = useSettingsContext()

  const isRTL = settings.themeDirection === 'rtl'

  const notistackRef = useRef(null)

  return (
    <NotistackProvider
      ref={notistackRef}
      maxSnack={5}
      autoHideDuration={null}
      TransitionComponent={isRTL ? Collapse : undefined}
      variant='success' // Set default variant
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      iconVariant={{
        info: (
          <StyledIcon color='info'>
            <Iconify icon='eva:info-fill' width={24} />
          </StyledIcon>
        ),
        success: (
          <StyledIcon color='success'>
            <Iconify icon='eva:checkmark-circle-2-fill' width={24} />
          </StyledIcon>
        ),
        warning: (
          <StyledIcon color='warning'>
            <Iconify icon='eva:alert-triangle-fill' width={24} />
          </StyledIcon>
        ),
        error: (
          <StyledIcon color='error'>
            <Iconify icon='solar:danger-bold' width={24} />
          </StyledIcon>
        ),
      }}
      Components={{
        default: StyledNotistack,
        info: StyledNotistack,
        success: StyledNotistack,
        warning: StyledNotistack,
        error: StyledNotistack,
        loading: LoadingNotistack,
      }}
      // with close as default
      action={(snackbarId) => (
        <IconButton size='small' onClick={() => closeSnackbar(snackbarId)} sx={{ p: 0.5 }}>
          <Iconify width={16} icon='mingcute:close-line' />
        </IconButton>
      )}
    >
      {children}
    </NotistackProvider>
  )
}

SnackbarProvider.propTypes = {
  children: PropTypes.node,
}
